import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Draggable, DragDropContext, Droppable } from "react-beautiful-dnd";

import UIAside from "components/UI/Aside";
import IconDragAndDrop from "components/Icon/DragAndDrop";
import FeatureRenderer from "components/Feature/RendererContainer";

const Option = styled.li.attrs({ className: "list-drag-drop-picker__option" })`
  background: ${props => props.theme.colors.white};
  border-radius: 2px;
  border: ${props => `1px solid ${props.theme.colors.iron}`};
  color: ${props => props.theme.colors.mako};
  cursor: pointer;
  height: 48px;
  line-height: 20px;
  margin: 0 0 12px 0;
  padding: 14px 16px;
  width: 100%;
`;

const ActiveOption = styled(Option).attrs({ className: "active" })`
  background: ${props => props.theme.colors.solera};
  border: none;
  color: ${props => props.theme.colors.white};
  opacity: ${props => (props.isDragging ? 0.8 : 1)};
`;

const Handler = styled(IconDragAndDrop).attrs({
  className: "list-drag-drop-picker__handler"
})`
  display: ${props => (props.hide ? "none" : "initial")};
  float: right;
`;

const Description = styled.p`
  margin: 0 0 24px 0;
`;

class ListDragDropPicker extends React.Component {
  constructor(props) {
    super(props);

    if (props.options === undefined) {
      this.state = { options: props.initialOptions || [] };
    }
  }

  handleDragEnd = ({ destination, source, draggableId }) => {
    if (!destination) return;

    const noChanges =
      destination.droppableId === source.droppableId &&
      destination.index === source.index;
    if (noChanges) return;

    const options = this.props.options || this.state.options;
    const item = options.find(item => item.id === draggableId);

    if (this.props.onOrderChange) {
      this.props.onOrderChange(item, source.index, destination.index, this.props.currentWorkList);
    }
    if (this.props.options) return; // Controlled component

    const newOptions = [...this.state.options];
    newOptions.splice(source.index, 1);
    newOptions.splice(destination.index, 0, item);
    this.setState({ options: newOptions });
  };

  renderOption = (item, index) => {
    const option = (
      <Draggable key={item.id} draggableId={item.id} index={index}>
        {(provided, snapshot) =>
          item.active ? (
            <ActiveOption
              {...provided.draggableProps}
              innerRef={provided.innerRef}
              isDragging={snapshot.isDragging}
              onClick={() => this.props.onItemClick(item)}
            >
              {item.id}
              <Handler
                {...provided.dragHandleProps}
                isDragging={snapshot.isDragging}
              />
            </ActiveOption>
          ) : (
            <Option
              {...provided.draggableProps}
              innerRef={provided.innerRef}
              onClick={() => this.props.onItemClick(item)}
            >
              {item.id}
              <Handler {...provided.dragHandleProps} hide />
            </Option>
          )
        }
      </Draggable>
    );

    if (item.id === "Actions") {
      return (
        <FeatureRenderer key={item.id} feature="FeesActions">
          {option}
        </FeatureRenderer>
      );
    }
    return option;
  };

  render() {
    let options = this.props.options || this.state.options;
    options = options.filter(
      (v,i,a) =>
        a.findIndex(
          t=>(t.id === v.id)
        ) === i
    );
    const { title, float, isOpen, onClose } = this.props;
    return (
      <UIAside title={title} float={float} isOpen={isOpen} onClose={onClose}>
        {this.props.description ? (
          <Description>{this.props.description}</Description>
        ) : null}

        <DragDropContext onDragEnd={this.handleDragEnd}>
          <Droppable droppableId="listDragDropPickerDroppable">
            {provided => (
              <ul {...provided.droppableProps} ref={provided.innerRef}>
                {options.map(this.renderOption)}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      </UIAside>
    );
  }
}

ListDragDropPicker.propTypes = {
  description: PropTypes.string,
  initialOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired
    })
  ),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired
    })
  ),
  onOrderChange: PropTypes.func,
  onItemClick: PropTypes.func,

  // UIAside
  title: PropTypes.string.isRequired,
  float: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func
};

ListDragDropPicker.defaultProps = {
  onItemClick: () => {}
};

export default ListDragDropPicker;
